<template>
	<section class="user-settings">
		<div class="user-settings__container">
			<!-- JustWatch Pro -->
			<ProWebButton v-if="!userStore.isPremium && languageStore.isProWebLocale" class="webapp-pro-button" />

			<!-- My Streaming services -->
			<BasicItem class="user-settings__providers" @click="openProviderSelection">
				<template #start>
					<FontAwesomeIcon :icon="faPlay" />
				</template>
				<div class="basic-item-label">
					{{ $t('WEBAPP_HYBRIDAPP_SETTINGS_PROVIDERS') }}
					<span class="user-settings__current-settings">
						{{ `${descriptionProviderList.join(',')} ` }}
						{{
							descriptionProviderListRest > 0
								? ` + ${descriptionProviderListRest} ${$t('WEBAPP_ACCOUNTMODAL_MAIN_PROVIDERS_MORE')}`
								: ''
						}}
					</span>
				</div>
			</BasicItem>

			<!-- Login Manager -->
			<BasicItem @click="openLoginManager">
				<template #start>
					<FontAwesomeIcon :icon="faUser" />
				</template>
				<div class="basic-item-label">{{ $t('WEBAPP_LOGIN_MANAGER_TITLE') }}</div>
			</BasicItem>

			<!-- Connect your TV -->
			<BasicItem @click="navigateTvLogin" detail="false">
				<template #start>
					<FontAwesomeIcon :icon="faTv" />
				</template>
				<div class="basic-item-label">{{ $t('WEBAPP_CONNECT_TV_LOGIN_HEADER') }}</div>
			</BasicItem>

			<!-- Country -->
			<BasicItem @click="openCountrySelection">
				<template #start>
					<FontAwesomeIcon :icon="faGlobeEurope" />
				</template>

				<div class="basic-item-label">
					{{ $t('WEBAPP_HYBRIDAPP_SETTINGS_REGIONS') }}
					<span class="user-settings__current-settings">{{ selectedCountryName }}</span>
				</div>
			</BasicItem>

			<!-- Language -->
			<BasicItem @click="openLanguageSelection">
				<template #start>
					<FontAwesomeIcon :icon="faLanguage" />
				</template>
				<div class="basic-item-label">
					{{ $t('WEBAPP_ACCOUNTMODAL_LANGUAGE_TITLE') }}
					<span class="user-settings__current-settings">{{ selectedLanguage }}</span>
				</div>
			</BasicItem>

			<!-- JustWatch Pro -->
			<BasicItem v-if="showProFeaturesFlag" @click="openSubscriptionView">
				<template #start>
					<FontAwesomeIcon :icon="jwCrownIcon" />
				</template>
				<div class="basic-item-label">{{ $t('WEBAPP_FREEMIUM_SUBSCRIPTIONS') }}</div>
			</BasicItem>

			<!-- FAQ Page -->
			<BasicItem @click="openFAQPage">
				<template #start>
					<FontAwesomeIcon :icon="faQuestionCircle" />
				</template>
				<div class="basic-item-label">{{ $t('WEBAPP_STATIC_FAQ') }}</div>
			</BasicItem>
		</div>

		<!-- UserSettings Footer -->
		<div class="user-settings__footer user-settings__footer-divider">
			<div
				class="user-settings__footer-item user-settings__footer-item--logout"
				v-if="userStore.isLoggedIn && isTitlePage"
				@click="handleSwitchInterface"
			>
				{{
					userStore.showOldTitlePage
						? $t('WEBAPP_SWITCH_TO_NEW_INTERFACE')
						: $t('WEBAPP_SWITCH_BACK_TO_OLD_INTERFACE')
				}}
			</div>

			<!-- Hide Seen and Disliked Titles -->
			<div class="user-settings__footer-images user-settings__hide-seen-filter" v-if="showProFeaturesFlag">
				<div class="basic-item-label">
					{{ $t('WEBAPP_HIDE_SEEN_DISLIKE_TITLES') }} <FreemiumFilterBadge size="sm" />
					<span class="user-settings__current-settings">{{ $t('WEBAPP_HIDE_SEEN_DISLIKE_SUBTITLE') }}</span>
				</div>
				<CheckboxInput
					class="basic-item--checkbox"
					:disabled="!userStore.isPremium"
					:modelValue="userStore.hideSeenDislikedTitles"
					@update:modelValue="checkSeenDislikeFilter"
				/>
			</div>

			<!-- Log Out -->
			<div
				class="user-settings__footer-item user-settings__footer-item--logout"
				v-if="userStore.isLoggedIn"
				@click="handleLogout"
			>
				{{ $t('WEBAPP_ACCOUNTMODAL_MAIN_SIGNOUT') }}
			</div>

			<!--      Delete Account-->
			<div
				class="user-settings__footer-item"
				v-if="userStore.isLoggedIn"
				@click.prevent="handleDeleteAccountClick"
			>
				{{ $t('WEBAPP_LOGINS_DELETEACCOUNT') }}
			</div>
		</div>
	</section>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import {
	CountrySelectionModal,
	LanguageSelectionModal,
	LoginManager,
	LoginMessageModal,
	PackagesSelectionModal,
	ProModal,
} from '@/lazy-load-components';

import BasicItem from '@/components/mobile/BasicItem.vue';

import { COUNTRY_EXCEPTIONS } from '@/constants/web-locales.constant';

import { promptAlert, showAlert } from '@/helpers/vfm/alert-helper';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { saveLastProView } from '@/helpers/pro-web-helper';
import { fetchStaticPages, StaticPage } from '@/helpers/static-page-helper';
import { SnowplowContext, SnowplowFreemiumContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { UserAccountEvents } from '@/enums/events';
import { LoginProvider } from '@/enums/login';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';

import FreemiumFilterBadge from '@/components/filter-bar/FreemiumFilterBadge.vue';
import ProWebButton from '@/components/modals/account/ProWebButton.vue';
import { addToast } from '@/helpers/vfm/toast-helper';
import { toastSuccess, toastError } from '@/helpers/vfm/toast-helper';
import {
	CancelProSubscriptionDocument,
	CancelProSubscriptionMutation,
	CancelProSubscriptionMutationVariables,
} from '../modals/pro/graphql/mutations/CancelProSubscription.mutation';
import { captureMessageForSentry } from '@/libraries/sentry';
import { safeOpen } from '@/helpers/clickout-helper';
import { faGlobeEurope, faLanguage, faPlay, faQuestionCircle, faTv, faUser } from '@fortawesome/free-solid-svg-icons';
import { jwCrownIcon } from '@/plugins/global-fontawesome';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

import { usePurchaserInfo } from '@/features/jw-pro/composables/usePurchaserInfo';

import CheckboxInput from '@/ui-components/CheckboxInput.vue';
import { reauthenticateUserWithPassword, reauthenticateSocialAuth } from '@/helpers/user-helper';

interface TrackingExtras {
	label?: string;
	property?: string;
	value?: number;
	contexts?: SnowplowContext[];
}

const FreemiumSubscriptionStatusModal = defineAsyncComponent(
	() => import('@/components/modals/freemium/FreemiumSubscriptionStatusModal.vue')
);

const emit = defineEmits(['close']);

const userStore = useUserStore();
const routingStore = useRoutingStore();
const constantStore = useConstantStore();
const languageStore = useLanguageStore();

const router = useRouter();
const { t } = useI18n();

const staticPages = ref<StaticPage[]>([]);
const { activeSubscription } = usePurchaserInfo(() => userStore.jwLoginId);

const { mutate: cancelProUserSubscriptionMutate } = useMutation<
	CancelProSubscriptionMutation,
	CancelProSubscriptionMutationVariables
>(CancelProSubscriptionDocument);

const selectedProviders = computed(
	() =>
		userStore.settings.providers.map(provider =>
			constantStore.providers().find(key => key.shortName === provider)
		) ?? []
);

const selectedCountryName = computed(() => {
	const countryCode = COUNTRY_EXCEPTIONS[languageStore.country.toLowerCase()] || languageStore.country;
	return t(`WEBAPP_COUNTRY_${countryCode.slice(0, 2).toUpperCase()}`);
});

const selectedLanguage = computed(() => {
	const lang = userStore.persistedOverriddenLanguage || languageStore.language;
	return lang ? t(`WEBAPP_LANGUAGE_${lang.toUpperCase()}`) : '';
});

const descriptionProviderList = computed(() =>
	selectedProviders.value.slice(0, 3).map(provider => (provider ? ` ${provider.clearName}` : ''))
);

const descriptionProviderListRest = computed(() => {
	const selectedProvidersCount = (userStore.settings.providers ?? []).length;
	return selectedProvidersCount - 3;
});

const showProFeaturesFlag = computed(() => languageStore.isProWebLocale || userStore.isPremium);
const isTitlePage = computed(() => routingStore.activeRoute?.name?.toString().includes('app.detail'));

onMounted(() => fetchSettingItems());

watch(
	() => languageStore.locale,
	() => fetchSettingItems()
);

async function fetchSettingItems() {
	const pages = await fetchStaticPages(languageStore.locale, { language: languageStore.language });

	staticPages.value = pages.filter(
		page => !['STATIC_FAQ', 'STATIC_TV_LOGIN', 'STATIC_APPS'].includes(page.technical_name)
	);
}

function handleSwitchInterface() {
	trackEvent(
		userStore.showOldTitlePage
			? UserAccountEvents.SWITCH_TO_NEW_TITLE_PAGE
			: UserAccountEvents.SWITCH_TO_OLD_TITLE_PAGE,
		'user_account'
	);

	userStore.toggleShowOldTitlePage();

	setTimeout(() => VFMHelper.close(), 200);
}

async function handleLogout() {
	trackEvent(UserAccountEvents.LOGOUT_CLICKED, 'user_account', userStore.loggedInProviders[0]);

	const clickedButton = await showAlert({
		header: t('WEBAPP_LOGINS_LOGOUT'),
		message: t('WEBAPP_LOGINS_LOGOUT_CONFIRMATION'),
		buttons: [t('WEBAPP_CANCEL'), t('WEBAPP_LOGINS_MERGE_YES')],
	});

	const loggedInProvider = ['email'];
	if (clickedButton === 1) {
		if (userStore.loggedInProviders.length > 0) {
			loggedInProvider.splice(0, 1, userStore.loggedInProviders[0] as string);
		}
		await userStore.logout(true);

		//Close all modals and open autoclosable modal with success message
		ModalHelper.openModal(
			LoginMessageModal,
			{
				successType: 'logged-out',
				closeDelay: 2000,
			},
			{
				id: 'login-message-modal',
			}
		);

		VFMHelper.close();

		closeSettings();

		trackEvent(UserAccountEvents.LOGOUT_CONFIRMED, undefined, loggedInProvider[0]);
		emit('close');
	} else {
		trackEvent(UserAccountEvents.LOGOUT_CANCELLED, undefined, userStore.loggedInProviders[0]);
	}
}

function trackEvent(action: string, label?: string, property?: string, value?: number): void {
	const extras: TrackingExtras = (action => {
		switch (action) {
			case UserAccountEvents.COUNTRY_CLICKED:
				return {
					label: languageStore.country + '',
				};
			case UserAccountEvents.LANGUAGE_CLICKED:
				return {
					label: selectedLanguage.value + '',
				};
			case UserAccountEvents.PROVIDER_CLICKED:
				return {
					label,
					value,
					property,
				};
			case UserAccountEvents.LOGOUT_CLICKED:
				return {
					label: undefined,
					property,
				};
			case UserAccountEvents.LOGOUT_CONFIRMED:
				return {
					label: userStore.jwLoginId,
					property,
				};
			case UserAccountEvents.LOGOUT_CANCELLED:
				return {
					label: undefined,
					property,
				};
			default:
				return {};
		}
	})(action);

	TrackingHelper.trackEvent(
		'user_account',
		{
			action,
			property: extras.property,
			label: extras.label,
			value: extras.value,
		},
		extras.contexts
	);
}

async function cancelProUserSubscription() {
	try {
		await cancelProUserSubscriptionMutate();
	} catch (error) {}
}

async function deleteAccount() {
	try {
		if (userStore.isPremium) {
			await cancelProUserSubscription();
		}
		await userStore.deleteAccountPermanently();
		VFMHelper.close();

		await userStore.loadSettings('deleteAccount');

		// No-Op as to delete account you must sign in and signed in users are already onboarded
		userStore.markUserOnboarded();

		closeSettings();

		toastSuccess({
			message: t('WEBAPP_ACCOUNTMODAL_DELETE_ACCOUNT_SUCCESS'),
			duration: 4000,
		});
		trackEvent(UserAccountEvents.DELETE_CONFIRMED);
	} catch (error) {
		const err = error as Error & { code?: string };

		if (err.code === 'auth/requires-recent-login') {
			// If user logged in with password, then ask them for their password
			if (userStore.userInfo.loginProvider === LoginProvider.JUSTWATCH) {
				await promptAlert({
					header: t('WEBAPP_ACCOUNTMODAL_CONFIRM_DELETE_ACCOUNT'),
					inputs: [
						{
							type: 'password',
							name: 'password',
							placeholder: t('WEBAPP_LOGINS_EMAIL_REQUEST_ENTER_PASSWORD'),
						},
					],
					buttons: [
						{
							text: t('WEBAPP_CANCEL'),
							role: 'cancel',
						},
						{
							text: t('WEBAPP_ACCOUNTMODAL_DELETE_ACCOUNT'),
							handler: async data => {
								const isUserReauthenticated = await reauthenticateUserWithPassword(data.password);
								if (isUserReauthenticated) {
									// Retry deleting account after user has reauthenticated.
									deleteAccount();
								} else {
									toastError({
										message: t('WEBAPP_LOGINS_EMAIL_WRONG_PASSWORD'),
										duration: 4000,
									});
								}
							},
						},
					],
				});
			} else {
				const clickedButton = await showAlert({
					header: t('WEBAPP_LOGINS_DELETEACCOUNT_CONFIRM'),
					buttons: [t('WEBAPP_CANCEL'), t('WEBAPP_PACKAGES_ADDON_DESELECT_CONFIRM')],
				});
				if (clickedButton === 1) {
					const isUserReauthenticated = await reauthenticateSocialAuth(
						userStore.userInfo.loginProvider as string
					);
					if (isUserReauthenticated) {
						// Retry deleting account after user has reauthenticated.
						deleteAccount();
					} else {
						toastError({
							message: t('WEBAPP_ACCOUNTMODAL_DELETE_ACCOUNT_ERROR'),
							duration: 4000,
						});
					}
				}
			}
		}
	}
}

function openProContactPage() {
	safeOpen(
		'https://support.justwatch.com/hc/en-us/articles/360020663318-Who-can-I-contact-if-I-have-problems-with-my-JustWatch-Pro-subscription-'
	);
}

async function handleDeleteAccountClick() {
	trackEvent(UserAccountEvents.DELETE_CLICKED, 'user_account');
	const clickedButton = await showAlert({
		header: t('WEBAPP_LOGINS_DELETEACCOUNT_CONFIRM'),
		buttons: [t('WEBAPP_CANCEL'), t('WEBAPP_LOGINS_MERGE_YES')],
	});

	if (clickedButton === 1) {
		if (
			activeSubscription.value &&
			activeSubscription.value.auto_resume_date &&
			!['stripe', 'promotional'].includes(activeSubscription.value.store)
		) {
			captureMessageForSentry('[Subscription needs to be canceled before deleting an account]:', {
				where: 'UserSettingsView',
				category: 'pro-subscription',
			});
			return addToast({
				message: t('WEBAPP_CANCEL_YOUR_SUBSCRIPTION_BEFORE_DELETE', {
					cancelYourSubscriptionHelp: t('WEBAPP_CANCEL_YOUR_SUBSCRIPTION_HELP'),
				}) as string,
				duration: 5000,
				showCloseButton: false,
				buttons: [
					{
						text: t('WEBAPP_STATIC_FAQ'),
						handler: openProContactPage,
					},
				],
			});
		}

		await deleteAccount();
		emit('close');
	} else {
		trackEvent(UserAccountEvents.DELETE_CANCELLED);
	}
}

function checkSeenDislikeFilter() {
	if (userStore.isPremium) {
		TrackingHelper.trackEvent(
			'user_account',
			{
				action: 'hide_titles_clicked',
				value: Number(userStore.hideSeenDislikedTitles),
			},
			[new SnowplowFreemiumContext(userStore.isHideSeenDislikedTitlesEnabled ?? false)]
		);
		return userStore.setHideSeenDislikedTitles(!userStore.hideSeenDislikedTitles);
	}

	if (languageStore.isProWebLocale) {
		ModalHelper.openModal(
			ProModal,
			{},
			{
				cssClass: 'pro-jw-pro-modal',
			},
			'SettingsHideSeenDisliked',
			true
		);
		saveLastProView('SettingsHideSeenDisliked');
	}
}

function openProviderSelection() {
	ModalHelper.openModal(
		PackagesSelectionModal,
		{ providerPlace: ProviderFilterPlace.SETTINGS },
		{ cssClass: 'packages-selection-modal' },
		ProviderFilterPlace.SETTINGS,
		true
	);
	trackEvent(UserAccountEvents.PROVIDER_CLICKED, undefined, ProviderFilterPlace.SETTINGS);
}

function navigateTvLogin() {
	router.push({ name: 'app.tv' });
	closeSettings();
	TrackingHelper.trackEvent('Pageview', {});
	emit('close');
}

function openFAQPage() {
	router.push({ name: 'app.faq' });
	TrackingHelper.trackEvent('Pageview', {});
	setTimeout(() => closeSettings(), 200);
	emit('close');
}

function openLoginManager() {
	ModalHelper.openModal(LoginManager);
}

function openCountrySelection() {
	ModalHelper.openModal(CountrySelectionModal);
	trackEvent(UserAccountEvents.COUNTRY_CLICKED);
}

function openLanguageSelection() {
	ModalHelper.openModal(LanguageSelectionModal);
	trackEvent(UserAccountEvents.LANGUAGE_CLICKED);
}

function openSubscriptionView() {
	ModalHelper.openModal(FreemiumSubscriptionStatusModal);
}

function closeSettings() {
	VFMHelper.closeById('settings');
}
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

$item-background: $jw-color-darkgray--dark;

.user-settings {
	.basic-item {
		--background: none;
		font-size: 20px;
		--background-hover: #{lighten($item-background, 5%)};
		display: flex;
		align-items: center;
		cursor: pointer;

		.basic-item-label {
			margin: 10px 0;
		}

		svg {
			color: $jw-color-mediumblue--light;
			width: 20px;
		}
	}

	&__container {
		padding-bottom: 10px;
	}

	&__current-settings {
		display: inherit;
		letter-spacing: 0.4px;
		font-size: $font-size-small;
		margin-top: 5px;
		font-weight: normal;
		color: $jw-color-mediumgray--dark;
	}

	&__footer {
		margin: 0 30px;
		padding-top: 35px;
	}

	&__footer-divider {
		border-top: 1px solid $jw-divider-color;
	}

	&__footer-images {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 30px;
	}

	&__footer-item {
		color: $jw-color-mediumgray--dark;
		font-size: 15px;
		margin-bottom: 30px;
		cursor: pointer;
	}

	&__footer-item:hover {
		opacity: 0.7;
	}

	&__footer-item-version {
		font-weight: bold;
		color: $jw-color-white;
		font-size: 11px;
		text-transform: uppercase;
		opacity: unset;
	}
}

.webapp-pro-button {
	margin: 0 15px 12px;
}

@media only screen and (max-width: 480px) {
	.user-settings {
		&__footer {
			padding-bottom: 75px;
		}
	}
}
</style>
